class ZenkaiCarousel {
  constructor(root) {
    if (root.dataset.zkCarouselReady === 'true') return;

    this.root = root;
    this.slides = [...root.querySelectorAll('[data-zk-slide]')];
    this.dots = [...root.querySelectorAll('[data-zk-dot]')];
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.isVisible = true;
    this.pointerStartX = null;
    this.pointerStartY = null;
    this.timer = null;
    this.onVisibilityChange = () => (document.hidden ? this.stop() : this.start());
    this.onBreakpointChange = () => this.show(this.index, false);

    root.dataset.zkCarouselReady = 'true';
    root._zenkaiCarousel = this;
    root.querySelector('[data-zk-next]')?.addEventListener('click', () => this.show(this.index + 1));
    root.querySelector('[data-zk-previous]')?.addEventListener('click', () => this.show(this.index - 1));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.show(Number(dot.dataset.zkDot))));

    root.addEventListener('mouseenter', () => this.stop());
    root.addEventListener('mouseleave', () => this.start());
    root.addEventListener('focusin', () => this.stop());
    root.addEventListener('focusout', (event) => {
      if (!root.contains(event.relatedTarget)) this.start();
    });
    root.addEventListener('pointerdown', (event) => this.pointerDown(event), { passive: true });
    root.addEventListener('pointerup', (event) => this.pointerUp(event), { passive: true });
    root.addEventListener('pointercancel', () => this.resetPointer(), { passive: true });
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.mobileQuery.addEventListener?.('change', this.onBreakpointChange);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
          if (this.isVisible) this.start();
          else this.stop();
        },
        { threshold: [0, 0.2, 0.6] }
      );
      this.observer.observe(root);
    }

    this.show(this.index, false);
  }

  show(next, restart = true) {
    if (!this.slides.length) return;

    this.index = (next + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.querySelectorAll('a, button').forEach((control) => {
        control.tabIndex = active ? 0 : -1;
      });

      slide.querySelectorAll('video').forEach((video) => {
        const mobileImageIsActive = this.mobileQuery.matches && slide.classList.contains('has-mobile-media');
        if (!active || document.hidden || mobileImageIsActive) {
          video.pause();
          return;
        }
        video.play().catch(() => {});
      });
    });

    this.dots.forEach((dot, index) => {
      dot.setAttribute('aria-selected', String(index === this.index));
      dot.tabIndex = index === this.index ? 0 : -1;
    });

    if (restart) this.start();
  }

  pointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
    this.stop();
  }

  pointerUp(event) {
    if (this.pointerStartX === null || this.pointerStartY === null) return;

    const distanceX = event.clientX - this.pointerStartX;
    const distanceY = event.clientY - this.pointerStartY;
    this.resetPointer();

    if (Math.abs(distanceX) >= 48 && Math.abs(distanceX) > Math.abs(distanceY) * 1.2) {
      this.show(this.index + (distanceX < 0 ? 1 : -1));
    } else {
      this.start();
    }
  }

  resetPointer() {
    this.pointerStartX = null;
    this.pointerStartY = null;
    this.start();
  }

  start() {
    this.stop();
    if (
      this.root.dataset.autoplay === 'true' &&
      !this.reducedMotion &&
      !document.hidden &&
      this.isVisible &&
      this.slides.length > 1
    ) {
      this.timer = window.setInterval(() => this.show(this.index + 1), 5500);
    }
  }

  stop() {
    window.clearInterval(this.timer);
    this.timer = null;
  }

  destroy() {
    this.stop();
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.mobileQuery.removeEventListener?.('change', this.onBreakpointChange);
    delete this.root.dataset.zkCarouselReady;
    delete this.root._zenkaiCarousel;
  }
}

const initializeZenkaiCarousels = (scope = document) => {
  const carousels = [...scope.querySelectorAll('[data-zk-carousel]')];
  if (scope.matches?.('[data-zk-carousel]')) carousels.unshift(scope);
  carousels.forEach((carousel) => new ZenkaiCarousel(carousel));
};

initializeZenkaiCarousels();

document.addEventListener('shopify:section:load', (event) => initializeZenkaiCarousels(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  const carousels = [...event.target.querySelectorAll('[data-zk-carousel]')];
  if (event.target.matches?.('[data-zk-carousel]')) carousels.unshift(event.target);
  carousels.forEach((carousel) => carousel._zenkaiCarousel?.destroy());
});
